import { z } from "zod";
import { offsetDateTimeWithOptionalSecondsSchema } from "./primitives.js";
export const historyAnalyticsUnitSchema = z.enum([
    "prompt",
    "session",
    "task_run",
]);
// Agent-written bounds; see offsetDateTimeWithOptionalSecondsSchema.
const timeRangeSchema = z.object({
    from: offsetDateTimeWithOptionalSecondsSchema.optional(),
    to: offsetDateTimeWithOptionalSecondsSchema.optional(),
}).strict().superRefine((range, context) => {
    if (range.from && range.to && Date.parse(range.from) >= Date.parse(range.to)) {
        context.addIssue({
            code: "custom",
            message: "from must be earlier than to; ranges use [from,to)",
            path: ["to"],
        });
    }
});
const exclusionsSchema = z.object({
    includeScheduled: z.boolean().default(false),
    includeCanary: z.boolean().default(false),
}).strict().default({
    includeScheduled: false,
    includeCanary: false,
});
export const workerHistoryAnalyticsRequestSchema = z.discriminatedUnion("operation", [
    z.object({
        operation: z.literal("count_activity"),
        runId: z.string().trim().min(1),
        tenantSlug: z.string().trim().min(1),
        unit: historyAnalyticsUnitSchema,
        range: timeRangeSchema.default({}),
        actorScope: z.enum(["self", "authorised_actors"]).default("self"),
        exclusions: exclusionsSchema,
    }).strict(),
    z.object({
        operation: z.literal("busiest_time"),
        runId: z.string().trim().min(1),
        tenantSlug: z.string().trim().min(1),
        unit: historyAnalyticsUnitSchema,
        dimension: z.enum(["hour", "day_of_week"]),
        timeZone: z.string().trim().min(1).max(100),
        range: timeRangeSchema.default({}),
        actorScope: z.enum(["self", "authorised_actors"]).default("self"),
        exclusions: exclusionsSchema,
    }).strict(),
    z.object({
        operation: z.literal("channel_creation"),
        runId: z.string().trim().min(1),
        tenantSlug: z.string().trim().min(1),
        channel: z.string().trim().min(1).max(200),
        actorScope: z.enum(["self", "authorised_actors"]).default("self"),
        limit: z.number().int().min(1).max(10).default(5),
    }).strict(),
    z.object({
        operation: z.literal("run_outcomes"),
        runId: z.string().trim().min(1),
        tenantSlug: z.string().trim().min(1),
        range: timeRangeSchema.default({}),
        actorScope: z.enum(["self", "authorised_actors"]).default("self"),
        exclusions: exclusionsSchema,
    }).strict(),
    z.object({
        operation: z.literal("top_token_runs"),
        runId: z.string().trim().min(1),
        tenantSlug: z.string().trim().min(1),
        range: timeRangeSchema.default({}),
        exclusions: exclusionsSchema,
        limit: z.number().int().min(1).max(10).default(5),
    }).strict(),
]);
export const retainedHistoryBoundsSchema = z.object({
    firstRetainedAt: z.string().datetime({ offset: true }).nullable(),
    lastRetainedAt: z.string().datetime({ offset: true }).nullable(),
    note: z.literal("All-time means the retained control-plane history between these bounds; earlier or deleted data is not represented."),
}).strict();
const analyticsBaseSchema = z.object({
    unit: historyAnalyticsUnitSchema,
    unitDefinition: z.string().trim().min(1),
    // The server echoes the requested bounds as written.
    range: z.object({
        from: offsetDateTimeWithOptionalSecondsSchema.nullable(),
        to: offsetDateTimeWithOptionalSecondsSchema.nullable(),
        semantics: z.literal("[from,to)"),
    }).strict(),
    retainedData: retainedHistoryBoundsSchema,
    excluded: z.object({
        deletedSessions: z.literal(true),
        linkedRetrievalChildren: z.literal(true),
        scheduled: z.boolean(),
        canary: z.boolean(),
    }).strict(),
    actorDisclosure: z.enum(["self_only", "authorised_actor_ids"]),
});
export const workerHistoryAnalyticsResponseSchema = z.discriminatedUnion("operation", [
    analyticsBaseSchema.extend({
        operation: z.literal("count_activity"),
        counts: z.array(z.object({
            actorId: z.string().trim().min(1).optional(),
            actorLabel: z.string().trim().min(1).optional(),
            count: z.number().int().nonnegative(),
        }).strict()).max(100),
        total: z.number().int().nonnegative(),
    }).strict(),
    analyticsBaseSchema.extend({
        operation: z.literal("busiest_time"),
        dimension: z.enum(["hour", "day_of_week"]),
        timeZone: z.string().trim().min(1),
        buckets: z.array(z.object({
            key: z.string().trim().min(1),
            label: z.string().trim().min(1),
            count: z.number().int().nonnegative(),
        }).strict()).max(24),
        busiest: z.object({
            key: z.string().trim().min(1),
            label: z.string().trim().min(1),
            count: z.number().int().nonnegative(),
        }).strict().nullable(),
    }).strict(),
    z.object({
        operation: z.literal("channel_creation"),
        channelQuery: z.string().trim().min(1),
        matches: z.array(z.object({
            channelId: z.string().trim().min(1),
            channelName: z.string().trim().min(1),
            createdAt: z.string().datetime({ offset: true }),
            sourceSessionId: z.string().trim().min(1),
            sourceRunId: z.string().trim().min(1).optional(),
            actorId: z.string().trim().min(1).optional(),
            actorLabel: z.string().trim().min(1).optional(),
            canonicalLink: z.string().url().optional(),
        }).strict()).max(10),
        actorDisclosure: z.enum(["self_only", "authorised_actor_ids"]),
    }).strict(),
    analyticsBaseSchema.extend({
        operation: z.literal("run_outcomes"),
        unit: z.literal("task_run"),
        statuses: z.array(z.object({
            status: z.enum(["queued", "running", "completed", "failed", "cancelled"]),
            count: z.number().int().nonnegative(),
        }).strict()).max(5),
        total: z.number().int().nonnegative(),
    }).strict(),
    analyticsBaseSchema.extend({
        operation: z.literal("top_token_runs"),
        unit: z.literal("task_run"),
        actorDisclosure: z.literal("self_only"),
        runs: z.array(z.object({
            runId: z.string().trim().min(1),
            sessionId: z.string().trim().min(1),
            title: z.string().trim().min(1).max(500),
            prompt: z.string().max(500),
            status: z.enum(["queued", "running", "completed", "failed", "cancelled"]),
            totalTokens: z.number().int().nonnegative(),
            createdAt: z.string().datetime({ offset: true }),
            finishedAt: z.string().datetime({ offset: true }).nullable(),
            sessionUrl: z.string().url().optional(),
        }).strict()).max(10),
    }).strict(),
]);
